/**
 * Lead submission boundary.
 *
 * All submission logic lives behind this interface so a real endpoint
 * (HubSpot Forms API, CRM webhook, email API) can be wired in one place without
 * touching the form UI. No submission is treated as delivered unless a
 * transport returns successfully.
 */

import { isHubSpotConfigured, submitHubSpotContact } from "@/lib/hubspot";
import { normalizeUsCanadaPhone } from "@/lib/phone";

export type LeadPayload = {
  name: string;
  phone: string;
  email: string;
  postalCode: string;
  roofingNeed: string;
  timeline: string;
  message: string;
  /** Marketing attribution captured from the URL / referrer. */
  attribution: Attribution;
  submittedAt: string;
  locale: string;
  pageUrl: string;
};

export type Attribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  referrer?: string;
};

const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
] as const;

const STORAGE_KEY = "revolin.attribution";
const LEAD_ENDPOINT = (import.meta.env.VITE_LEAD_ENDPOINT as string | undefined)?.trim();

/** Capture Google Ads / UTM parameters on first landing and persist them. */
export function captureAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const fresh: Attribution = {};
  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key);
    if (value) fresh[key] = value;
  }
  if (document.referrer) fresh.referrer = document.referrer;

  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    const prior: Attribution = stored ? JSON.parse(stored) : {};
    const merged = { ...prior, ...fresh };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    return merged;
  } catch {
    return fresh;
  }
}

export interface LeadTransport {
  send(payload: LeadPayload): Promise<void>;
}

export class LeadTransportUnavailableError extends Error {
  constructor() {
    super("Lead submission transport is not configured");
    this.name = "LeadTransportUnavailableError";
  }
}

/**
 * Builds the HubSpot message body: every quote field followed by the
 * visitor's own message. Labels stay English so ops reads consistent summaries.
 */
function buildHubSpotMessage(payload: LeadPayload): string {
  const lines = [
    `Name: ${payload.name.trim()}`,
    `Phone: ${payload.phone}`,
    `Email: ${payload.email.trim()}`,
    `Postal Code: ${payload.postalCode.trim().toUpperCase()}`,
    `Roofing Need: ${payload.roofingNeed}`,
    `Timeline: ${payload.timeline}`,
    `Locale: ${payload.locale}`,
    `Page: ${payload.pageUrl}`,
  ];

  const attributionEntries = Object.entries(payload.attribution).filter(
    ([, value]) => typeof value === "string" && value.trim(),
  );
  if (attributionEntries.length > 0) {
    lines.push(
      `Attribution: ${attributionEntries.map(([key, value]) => `${key}=${value}`).join("; ")}`,
    );
  }

  const message = payload.message?.trim();
  return message ? `${lines.join("\n")}\n\nMessage:\n${message}` : lines.join("\n");
}

const endpointTransport: LeadTransport | null = LEAD_ENDPOINT
  ? {
      async send(payload) {
        const response = await fetch(LEAD_ENDPOINT, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(payload),
          credentials: "same-origin",
        });

        if (!response.ok) {
          throw new Error(`Lead endpoint returned HTTP ${response.status}`);
        }
      },
    }
  : null;

const hubspotTransport: LeadTransport | null = isHubSpotConfigured()
  ? {
      async send(payload) {
        const [firstName, ...rest] = payload.name.trim().split(/\s+/);
        const phone = normalizeUsCanadaPhone(payload.phone);
        await submitHubSpotContact({
          firstName: firstName || "-",
          lastName: rest.join(" ") || "-",
          email: payload.email,
          phoneNumber: phone,
          message: buildHubSpotMessage({ ...payload, phone }),
        });
      },
    }
  : null;

/**
 * Prefer a dedicated lead endpoint when set. HubSpot Forms API is used as the
 * primary transport when HubSpot is configured and no endpoint is set.
 * When both are set, the endpoint is required and HubSpot is best-effort
 * (matches the blueprint QuoteForm mirror behaviour).
 */
function createDefaultTransport(): LeadTransport | null {
  if (endpointTransport && hubspotTransport) {
    return {
      async send(payload) {
        await endpointTransport.send(payload);
        try {
          await hubspotTransport.send(payload);
        } catch (error) {
          console.error("[leads] HubSpot mirror failed:", error);
        }
      },
    };
  }
  return endpointTransport ?? hubspotTransport;
}

let transport: LeadTransport | null = createDefaultTransport();

export function configureLeadTransport(next: LeadTransport) {
  transport = next;
}

export function isLeadTransportConfigured(): boolean {
  return transport !== null;
}

export async function submitLead(
  data: Omit<LeadPayload, "attribution" | "submittedAt" | "pageUrl">,
): Promise<void> {
  if (!transport) throw new LeadTransportUnavailableError();

  await transport.send({
    ...data,
    attribution: captureAttribution(),
    submittedAt: new Date().toISOString(),
    pageUrl: typeof window === "undefined" ? "" : window.location.href,
  });
}
